/**
 * CABCostPanel — Displays cost breakdown for CAB generation.
 *
 * Shows total cost, per-phase breakdown, and token usage.
 * Collapsible display in the action bar area.
 */

import React, { useState } from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { formatCost } from '../../utils/formatCost';
import { showTokens } from '../../utils/usageConfig';
import type { CABCostInfo } from '../../types/cab';

interface CABCostPanelProps {
  cost: CABCostInfo;
  visible: boolean;
  totalDurationSeconds?: number | null;
}

/** Format token count. */
function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return `${count}`;
}

/** Format a duration in seconds to a human-readable string (matches Build Mode). */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${mins}m ${remainingSeconds}s`;
}

/** Phase keys that belong to the CAB generation group (not user-triggered sub-operations). */
const GENERATION_PHASES = new Set([
  'file_processing',
  'code_analysis',
  'skeleton_map',
  'skeleton_reduce',
  'skeleton_assemble',
  'vector_indexing',
  'section_generation',
]);

/** Embedding phase keys — indexing cost paid to OpenAI for dense embeddings. */
const EMBEDDING_PHASES = [
  // AI chat / edit retrieval embedding
  'cab_item_embedding_first',
  'cab_item_embedding_update',
  'cab_item_embedding_restore',
  'cab_item_query_embedding',  // query-time embedding (AI chat / edit, no index rebuild)
  // Blueprint generation embedding (document + code-summary corpus index + queries)
  'cab_gen_embedding_indexing',
  'cab_gen_query_embedding',
] as const;

export const CABCostPanel: React.FC<CABCostPanelProps> = ({ cost, visible, totalDurationSeconds }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!visible || cost.total_usd === 0) return null;

  // Add latency from chat and edit operations (stored as latency_ms in breakdown)
  // to the base generation duration so the displayed time covers all AI work.
  const breakdown = cost.breakdown || {};
  const chatLatencyMs = (breakdown['cab_chat_review'] as any)?.latency_ms || 0;
  const editLatencyMs = (breakdown['ai_edit_review'] as any)?.latency_ms || 0;
  const extraSeconds = (chatLatencyMs + editLatencyMs) / 1000;
  const effectiveDuration =
    (totalDurationSeconds != null && totalDurationSeconds > 0)
      ? totalDurationSeconds + extraSeconds
      : extraSeconds > 0 ? extraSeconds : null;

  return (
    <div className="border-t border-border-secondary">
      {/* Collapsed summary */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="
          w-full px-bg-v2-xl py-bg-v2-sm
          flex items-center justify-between
          text-text-tertiary hover:text-text-secondary
          transition-colors
        "
      >
        <div className="flex items-center gap-bg-v2-sm">
          <Icon name={IconName.Dollar} size="xs" inheritColor />
          <Text variant="text-xs" weight="medium" className="text-text-tertiary">
            Generation Cost: {formatCost(cost.total_usd)}
            {effectiveDuration != null && effectiveDuration > 0 && (
              <span className="text-text-quaternary">
                {' '}·{' '}{formatDuration(effectiveDuration)}
              </span>
            )}
          </Text>
        </div>
        <Icon
          name={IconName.ChevronDown}
          size="xs"
          className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          inheritColor
        />
      </button>

      {/* Expanded breakdown */}
      {isExpanded && (
        <div className="px-bg-v2-xl pb-bg-v2-md">
          {/* Grouped cost breakdown */}
          {(() => {
            // Sum all generation phase costs into one row
            const generationCost = Object.entries(breakdown).reduce((sum, [phase, data]) => {
              if (!GENERATION_PHASES.has(phase)) return sum;
              return sum + ((data as any).cost_usd || 0);
            }, 0);

            const aiEditData = breakdown['ai_edit_review'] as any;
            const aiEditCost = aiEditData?.cost_usd || 0;
            const aiEditRequests = aiEditData?.requests || 0;

            const chatData = breakdown['cab_chat_review'] as any;
            const chatCost = chatData?.cost_usd || 0;
            const chatRequests = chatData?.requests || 0;

            const rows: { label: string; requests: number | null; cost: number }[] = [];

            if (generationCost > 0) {
              rows.push({ label: 'Blueprint Generation', requests: 1, cost: generationCost });
            }
            if (aiEditCost > 0) {
              rows.push({ label: 'AI Edit Review', requests: aiEditRequests, cost: aiEditCost });
            }
            if (chatCost > 0) {
              rows.push({ label: 'Chat AI Assist', requests: chatRequests, cost: chatCost });
            }

            // Embedding phases — aggregated into one row across all sub-phases.
            // Each AI chat/edit retrieval call contributes +1 request regardless
            // of whether the index was rebuilt (cab_item_embedding_first) or only
            // the query was embedded (cab_item_query_embedding).
            const embeddingTotals = EMBEDDING_PHASES.reduce(
              (acc, phase) => {
                const phaseData = breakdown[phase] as any;
                if (!phaseData) return acc;
                acc.cost += phaseData.cost_usd || 0;
                acc.requests += phaseData.requests || 0;
                return acc;
              },
              { cost: 0, requests: 0 },
            );
            if (embeddingTotals.requests > 0) {
              rows.push({
                label: 'CAB Indexing Cost',
                requests: embeddingTotals.requests,
                cost: embeddingTotals.cost,
              });
            }
            return (
              <div className="mb-bg-v2-sm">
                {rows.map(({ label, requests, cost: rowCost }) => (
                  <div key={label} className="flex justify-between items-center py-bg-v2-xxs">
                    <Text variant="text-xs" className="text-text-tertiary">
                      {label}
                      {requests !== null && (
                        <span className="text-text-quaternary ml-bg-v2-xs">
                          ({requests} {requests === 1 ? 'Request' : 'Requests'})
                        </span>
                      )}
                    </Text>
                    <Text variant="text-xs" weight="medium" className="text-text-secondary">
                      {formatCost(rowCost)}
                    </Text>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Token usage with costs */}
          <div className={showTokens ? "border-t border-border-secondary pt-bg-v2-sm" : ""}>
            {showTokens && (
              <>
                <div className="flex justify-between items-center py-bg-v2-xxs">
                  <Text variant="text-xs" className="text-text-tertiary">
                    Input Tokens
                    <span className="text-text-quaternary ml-bg-v2-xs">
                      ({formatTokens(cost.token_usage?.total_input_tokens || 0)})
                    </span>
                  </Text>
                  <Text variant="text-xs" weight="medium" className="text-text-secondary">
                    {formatCost(cost.token_usage?.input_tokens_cost || 0)}
                  </Text>
                </div>
                <div className="flex justify-between items-center py-bg-v2-xxs">
                  <Text variant="text-xs" className="text-text-tertiary">
                    Output Tokens
                    <span className="text-text-quaternary ml-bg-v2-xs">
                      ({formatTokens(cost.token_usage?.total_output_tokens || 0)})
                    </span>
                  </Text>
                  <Text variant="text-xs" weight="medium" className="text-text-secondary">
                    {formatCost(cost.token_usage?.output_tokens_cost || 0)}
                  </Text>
                </div>
                {(cost.token_usage?.total_cache_write_tokens || 0) > 0 && (
                  <div className="flex justify-between items-center py-bg-v2-xxs">
                    <Text variant="text-xs" className="text-text-tertiary">
                      Cache Creation
                      <span className="text-text-quaternary ml-bg-v2-xs">
                        ({formatTokens(cost.token_usage.total_cache_write_tokens)})
                      </span>
                    </Text>
                    <Text variant="text-xs" weight="medium" className="text-text-secondary">
                      {formatCost(cost.token_usage?.cache_write_tokens_cost || 0)}
                    </Text>
                  </div>
                )}
                {(cost.token_usage?.total_cache_read_tokens || 0) > 0 && (
                  <div className="flex justify-between items-center py-bg-v2-xxs">
                    <Text variant="text-xs" className="text-text-tertiary">
                      Cache Read
                      <span className="text-text-quaternary ml-bg-v2-xs">
                        ({formatTokens(cost.token_usage.total_cache_read_tokens)})
                      </span>
                    </Text>
                    <Text variant="text-xs" weight="medium" className="text-text-secondary">
                      {formatCost(cost.token_usage?.cache_read_tokens_cost || 0)}
                    </Text>
                  </div>
                )}
              </>
            )}

            {/* Total cost & duration */}
            <div className="flex justify-between items-center pt-bg-v2-xs mt-bg-v2-xs border-t border-border-secondary">
              <Text variant="text-xs" weight="semibold" className="text-text-secondary">
                Total Cost:
              </Text>
              <Text variant="text-xs" weight="semibold" className="text-text-secondary">
                {formatCost(cost.total_usd)}
              </Text>
            </div>
            {effectiveDuration != null && effectiveDuration > 0 && (
              <div className="flex justify-between items-center pt-bg-v2-xxs">
                <Text variant="text-xs" weight="semibold" className="text-text-secondary">
                  Total Time:
                </Text>
                <Text variant="text-xs" weight="semibold" className="text-text-secondary">
                  {formatDuration(effectiveDuration)}
                </Text>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
